import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { BrandCard } from './BrandCard';

interface LoginFormProps {
  onLogin?: (username: string, password: string) => boolean | Promise<boolean>;
  onResendVerification?: () => Promise<void>;
  onEmailChange?: () => void;
  error?: string;
  /** Wrong email or password (HTTP 401): shows the page's own copy. */
  invalidCredentials?: boolean;
  emailNotVerified?: boolean;
  loading?: boolean;
  initialUsername?: string;
}

export function LoginForm({
  onLogin,
  onResendVerification,
  onEmailChange,
  error,
  invalidCredentials = false,
  emailNotVerified = false,
  loading = false,
  initialUsername,
}: LoginFormProps) {
  const { t } = useTranslation();
  const [username, setUsername] = useState(initialUsername ?? '');
  const [password, setPassword] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const isLoading = loading;

  // sendOnSignIn already dispatched an email; start with 'sent' + 30s cooldown.
  // Guard with functional update to avoid overwriting an in-flight 'sending' state.
  useEffect(() => {
    if (!emailNotVerified) return;
    setResendStatus((prev) => (prev === 'sending' ? prev : 'sent'));
    const timer = setTimeout(() => setResendStatus('idle'), 30_000);
    return () => clearTimeout(timer);
  }, [emailNotVerified]);

  const handleResendVerification = async () => {
    if (resendStatus !== 'idle' || !onResendVerification) return;
    setResendStatus('sending');
    try {
      await onResendVerification();
      setResendStatus('sent');
    } catch {
      // Silently reset on failure to allow retry
      setResendStatus('idle');
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setUsername(e.target.value);
    onEmailChange?.();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!username || !password || isLoading) {
      return;
    }

    try {
      if (onLogin) {
        await onLogin(username, password);
      }
    } catch (err) {
      console.error('Login failed:', err);
    }
  };

  const invalid = invalidCredentials ? 'true' : undefined;

  return (
    <BrandCard title={t('auth.login.title')} titleAs="h2">
      <form className="fc-stack" onSubmit={handleSubmit} autoComplete="on">
        {invalidCredentials ? (
          <div role="alert" className="fc-alert">
            <strong>{t('auth.login.invalidCredentialsTitle')}</strong>{' '}
            {t('auth.login.invalidCredentialsBody')}
          </div>
        ) : error ? (
          <div role="alert" className="fc-alert">
            {error}
          </div>
        ) : null}

        {emailNotVerified && (
          <div role="status" className="fc-alert">
            <p>{t('auth.login.emailNotVerified')}</p>
            <p>{t('auth.login.emailNotVerifiedHint')}</p>
            <button
              type="button"
              className="fc-link-button"
              onClick={handleResendVerification}
              disabled={resendStatus !== 'idle'}
            >
              {resendStatus === 'sent'
                ? t('auth.login.verificationResent')
                : resendStatus === 'sending'
                  ? t('auth.login.resendingVerification')
                  : t('auth.login.resendVerification')}
            </button>
          </div>
        )}

        <div className="fc-field">
          <label htmlFor="email">{t('auth.login.email')}</label>
          <input
            id="email"
            name="email"
            className="fc-input"
            data-testid="login-email"
            type="email"
            inputMode="email"
            autoCapitalize="none"
            autoComplete="email"
            aria-invalid={invalid}
            value={username}
            onChange={handleEmailChange}
            disabled={isLoading}
            required
          />
        </div>
        <div className="fc-field">
          <div className="fc-field-row">
            <label htmlFor="password">{t('auth.login.password')}</label>
            <Link to="/forgot-password">{t('auth.login.forgotPassword')}</Link>
          </div>
          <input
            id="password"
            name="password"
            className="fc-input"
            data-testid="login-password"
            type="password"
            autoComplete="current-password"
            aria-invalid={invalid}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
            required
          />
        </div>
        <button type="submit" className="fc-button" data-testid="login-submit" disabled={isLoading}>
          {isLoading ? t('auth.login.submitting') : t('auth.login.submit')}
        </button>
      </form>
    </BrandCard>
  );
}
